import { useEffect, useMemo, useRef } from "react";
import { MapContainer, Marker, Polyline, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { GpsAtTime } from "../lib/playback-gps";

const emptyCenter: [number, number] = [20, 0];

const CAR_SVG = `<svg class="vehicle-marker-car" viewBox="0 0 32 48" aria-hidden="true">
  <rect x="8" y="8" width="16" height="32" rx="5"/>
  <rect class="vehicle-marker-window" x="11" y="12" width="10" height="8" rx="1.5"/>
  <rect class="vehicle-marker-window" x="11" y="28" width="10" height="6" rx="1.5"/>
  <rect x="6" y="16" width="3" height="7" rx="1"/>
  <rect x="23" y="16" width="3" height="7" rx="1"/>
  <rect x="6" y="30" width="3" height="7" rx="1"/>
  <rect x="23" y="30" width="3" height="7" rx="1"/>
</svg>`;

function carIcon(gap: boolean): L.DivIcon {
  return new L.DivIcon({
    className: "vehicle-marker-wrap",
    html: `<span class="vehicle-marker vehicle-marker--${gap ? "offline" : "moving"} is-selected"><span class="vehicle-marker-pulse"></span><span class="vehicle-marker-rot">${CAR_SVG}</span></span>`,
    iconSize: [44, 44],
    iconAnchor: [22, 22],
  });
}

function Follow({ lat, lon, enabled }: { lat: number; lon: number; enabled: boolean }) {
  const map = useMap();
  const primed = useRef(false);
  useEffect(() => {
    if (!enabled) {
      primed.current = false;
      return;
    }
    if (!primed.current) {
      primed.current = true;
      map.setView([lat, lon], Math.max(map.getZoom(), 16), { animate: true });
      return;
    }
    map.panTo([lat, lon], { animate: true, duration: 0.35 });
  }, [enabled, lat, lon, map]);
  return null;
}

function PlaybackMarker({ position, heading, gap }: { position: [number, number]; heading: number; gap: boolean }) {
  const markerRef = useRef<L.Marker | null>(null);
  const icon = useMemo(() => carIcon(gap), [gap]);
  useEffect(() => {
    const rot = markerRef.current?.getElement()?.querySelector<HTMLElement>(".vehicle-marker-rot");
    if (rot) rot.style.transform = `rotate(${Math.round(heading)}deg)`;
  }, [heading, icon]);
  return <Marker ref={markerRef} position={position} icon={icon} zIndexOffset={800} />;
}

function FitOnce({ points }: { points: Array<[number, number]> }) {
  const map = useMap();
  const done = useRef(false);
  useEffect(() => {
    done.current = false;
  }, [points.length > 0 ? `${points[0]?.[0]}:${points[points.length - 1]?.[0]}` : ""]);
  useEffect(() => {
    if (done.current || points.length === 0) return;
    done.current = true;
    if (points.length === 1) {
      map.setView(points[0]!, 15);
      return;
    }
    map.fitBounds(points, { padding: [28, 28], maxZoom: 16 });
  }, [map, points]);
  return null;
}

export function PlaybackMap({
  position,
  trail,
  gap,
}: {
  position: GpsAtTime | null;
  trail: Array<[number, number]>;
  gap: boolean;
}) {
  const heading = position?.heading_deg ?? 0;
  return (
    <section className="playback-map" aria-label="Recording GPS map">
      <MapContainer center={position ? [position.lat, position.lon] : emptyCenter} zoom={position ? 16 : 3} className="playback-map-root" scrollWheelZoom>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitOnce points={trail.length > 0 ? trail : position ? [[position.lat, position.lon]] : []} />
        {position ? <Follow lat={position.lat} lon={position.lon} enabled /> : null}
        {trail.length >= 2 ? (
          <Polyline positions={trail} interactive={false} pathOptions={{ color: "#3d8bfd", weight: 5, opacity: 0.9 }} />
        ) : null}
        {position ? (
          <PlaybackMarker position={[position.lat, position.lon]} heading={heading} gap={gap} />
        ) : null}
      </MapContainer>
      <div className={`playback-map-hud${gap ? " is-gap" : ""}`}>
        {position
          ? gap
            ? `GPS gap — holding last fix ${position.lat.toFixed(5)}, ${position.lon.toFixed(5)}`
            : `${position.lat.toFixed(5)}, ${position.lon.toFixed(5)}${position.speed_kmh != null ? ` · ${position.speed_kmh.toFixed(1)} km/h` : ""}`
          : "No GPS for this recording"}
      </div>
    </section>
  );
}
